const serverUrl =
    process.env.NODE_ENV === "development"
        ? "http://localhost:6970/api"
        : "https://questie.titolounge.dev/api";

export enum Method {
    GET = "GET",
    PUT = "PUT",
    POST = "POST",
    PATCH = "PATCH",
    DELETE = "DELETE",
}

type Res<T> = GoodRes<T> | BadRes;

interface GoodRes<T> {
    ok: true;
    body: T;
}

interface BadRes {
    ok: false;
    status: number;
    message: string;
}

const request = async <T>(
    path: string,
    method: Method,
    headers: Record<string, any>,
    body: any,
    attemptRefresh = true,
): Promise<Res<T>> => {
    const accessToken = sessionStorage.getItem("accessToken");

    const opts = {
        method: method,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            ...headers,
        },
    } as Record<string, any>;

    if (body && [Method.PUT, Method.POST, Method.PATCH].includes(method)) {
        opts.headers["Content-Type"] = "application/json";
        opts["body"] = JSON.stringify(body);
    }

    const res = await fetch(`${serverUrl}${path}`, opts);

    if (res.status === 401) {
        if (!attemptRefresh) {
            sessionStorage.clear();
            localStorage.clear();
            window.location.href = "/login";

            return {
                ok: false,
                status: res.status,
                message: "Unauthorized",
            };
        }

        const ok = await doRefresh();

        if (!ok) {
            throw Error("failed to authorize");
        }

        return request(path, method, headers, body, false);
    }

    if (res.status === 204) {
        return {
            ok: true,
            body: {} as T,
        };
    }

    const _body = await res.json();
    if (res.status >= 400) {
        if (!_body.message)
            throw Error(`bad request (${res.status}) is missing 'message'`);

        return {
            ok: false,
            status: res.status,
            message: _body.message,
        };
    }

    return {
        ok: true,
        body: _body,
    };
};

const doRefresh = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = "/login";
        return false;
    }

    try {
        const res = await fetch(`${serverUrl}/auth/refresh`, {
            headers: {
                Authorization: `Bearer ${refreshToken}`,
            },
        });

        if (!res.ok) {
            throw Error(
                `bad response code (${res.status}) received from token refresh`,
            );
        }

        const body = await res.json();

        if (!body.accessToken)
            throw Error(`refresh response payload missing access token`);

        sessionStorage.setItem("accessToken", body.accessToken);
    } catch (err) {
        console.error("failed to perform refresh:", err);
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = "/login";
        return false;
    }

    return true;
};

export const get = async <T>(url: string) => {
    return request<T>(url, Method.GET, {}, null);
};

export const put = async <T>(url: string, body: any) => {
    return request<T>(url, Method.PUT, {}, body);
};

export const post = async <T>(url: string, body: any) => {
    return request<T>(url, Method.POST, {}, body);
};

export const patch = async <T>(url: string, body: any) => {
    return request<T>(url, Method.PATCH, {}, body);
};

export const _delete = async <T>(url: string) => {
    return request<T>(url, Method.DELETE, {}, null);
};
